/**
 * Schedule expansion + slot formatting for the public "Request a demo"
 * web page. Same algorithms as the mobile `parseDemoBookingFromPublic.ts`
 * + `demoBookingRules.ts` — kept in plain JS to avoid TS plumbing on the
 * marketing site.
 *
 * The backend `GET /api/demo-request/availability` returns:
 *   { timezone, weeksAhead, schedule, bookedSlots }
 *
 * We expand `schedule` into concrete `{date, time}` slots for the next
 * `weeksAhead` weeks (Irish local calendar by default), then mark every
 * entry in `bookedSlots` as occupied.
 */

import { FetchDemoBookingAvailabilityCall } from "../../redux/api/demoRequest.api";

export const DEFAULT_DEMO_TIMEZONE = "Europe/Dublin";
export const DEMO_BOOKING_WEEKS_AHEAD = 3;

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

const WEEKDAY_TO_INDEX = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

const INTL_WEEKDAY_TO_INDEX = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function isValidHHMM(value) {
  return typeof value === "string" && HHMM_RE.test(value);
}

/** Accept "16:00", "14:30", "2pm", "2:00 pm", "9:30 AM", … */
export function normalizeTime(value) {
  if (value == null) return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  const ampm = raw.endsWith("am") ? "am" : raw.endsWith("pm") ? "pm" : null;
  const core = ampm ? raw.slice(0, -2).trim() : raw;
  const m = core.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return isValidHHMM(raw) ? raw : null;
  let hh = Number(m[1]);
  const mm = m[2] != null ? Number(m[2]) : 0;
  if (Number.isNaN(hh) || Number.isNaN(mm) || mm < 0 || mm > 59) return null;
  if (ampm) {
    if (hh < 1 || hh > 12) return null;
    if (ampm === "pm" && hh !== 12) hh += 12;
    if (ampm === "am" && hh === 12) hh = 0;
  } else if (hh < 0 || hh > 23) {
    return null;
  }
  const canonical = `${pad2(hh)}:${pad2(mm)}`;
  return isValidHHMM(canonical) ? canonical : null;
}

function normalizeWeekdayIndex(value) {
  const key = String(value || "").trim().toLowerCase();
  return key in WEEKDAY_TO_INDEX ? WEEKDAY_TO_INDEX[key] : null;
}

function getYmdInTimezone(date, timeZone) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function parseYmd(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysYmd(ymd, days) {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

function getWeekdayIndexInTimezone(ymd, timeZone) {
  const [y, m, day] = ymd.split("-").map(Number);
  const utcNoon = new Date(Date.UTC(y, m - 1, day, 12, 0, 0));
  const short = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(utcNoon);
  return INTL_WEEKDAY_TO_INDEX[short] ?? -1;
}

export function expandScheduleToSlots(schedule, weeksAhead, timeZone) {
  const rules = (Array.isArray(schedule) ? schedule : [])
    .map((row) => ({
      weekday: normalizeWeekdayIndex(row?.weekday),
      times: (Array.isArray(row?.times) ? row.times : [])
        .map(normalizeTime)
        .filter(Boolean),
    }))
    .filter((r) => r.weekday != null && r.times.length > 0);

  if (rules.length === 0) return [];

  const today = getYmdInTimezone(new Date(), timeZone);
  const last = addDaysYmd(today, weeksAhead * 7);
  const out = [];
  const seen = new Set();
  let cursor = today;
  while (cursor <= last) {
    const dow = getWeekdayIndexInTimezone(cursor, timeZone);
    for (const rule of rules) {
      if (rule.weekday !== dow) continue;
      for (const time of rule.times) {
        const key = `${cursor}_${time}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ date: cursor, time });
      }
    }
    cursor = addDaysYmd(cursor, 1);
  }
  out.sort(
    (a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)
  );
  return out;
}

function normalizeBookedEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const date = String(entry.date || "").trim();
  if (!YMD_RE.test(date)) return null;
  const time = normalizeTime(entry.time);
  if (!time) return null;
  return { date, time };
}

export function formatTimezoneDisplay(timeZone = DEFAULT_DEMO_TIMEZONE) {
  try {
    const parts = new Intl.DateTimeFormat("en-IE", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const offset =
      parts.find((p) => p.type === "timeZoneName")?.value?.replace(
        /Greenwich Mean Time/i,
        "GMT"
      ) || "GMT";
    if (timeZone === "Europe/Dublin") return `Irish local time (${offset})`;
    return `${timeZone} (${offset})`;
  } catch {
    return timeZone;
  }
}

export function slotKey(slot) {
  return `${slot.date}_${slot.time}`;
}

/** "16:00" → "4:00 PM" */
export function formatTimeLabel(time) {
  if (!isValidHHMM(time)) return String(time || "");
  const [hhStr, mmStr] = time.split(":");
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${pad2(mm)} ${ampm}`;
}

/** "2026-06-15" → "Monday, Jun 15" */
export function formatDateLabel(ymd) {
  return parseYmd(ymd).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function formatSlotLabel(slot) {
  return `${formatDateLabel(slot.date)} · ${formatTimeLabel(slot.time)}`;
}

/** Group flat slot list into per-day buckets for the picker UI. */
export function groupSlotsByDate(slots) {
  const map = new Map();
  for (const s of slots) {
    const prev = map.get(s.date) || [];
    if (!prev.includes(s.time)) prev.push(s.time);
    map.set(s.date, prev);
  }
  return [...map.entries()]
    .map(([date, times]) => ({ date, times: times.sort() }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Hit the backend availability endpoint and expand it into something
 * the picker can render. Always returns a complete shape (with empty
 * arrays on error) so the caller doesn't need a separate error branch.
 */
export async function fetchAvailability() {
  const empty = {
    slots: [],
    bookedSlots: [],
    timezone: DEFAULT_DEMO_TIMEZONE,
    timezoneLabel: formatTimezoneDisplay(DEFAULT_DEMO_TIMEZONE),
    fullyBooked: true,
  };

  const resp = await FetchDemoBookingAvailabilityCall();
  if (!resp || !Array.isArray(resp.schedule)) return empty;

  const timezone = String(resp.timezone || DEFAULT_DEMO_TIMEZONE).trim();
  const weeksAhead = Math.max(
    1,
    Math.min(12, Number(resp.weeksAhead) || DEMO_BOOKING_WEEKS_AHEAD)
  );
  const slots = expandScheduleToSlots(resp.schedule, weeksAhead, timezone);
  const validKeys = new Set(slots.map(slotKey));
  const bookedSlots = (Array.isArray(resp.bookedSlots) ? resp.bookedSlots : [])
    .map(normalizeBookedEntry)
    .filter(Boolean)
    .filter((b) => validKeys.has(slotKey(b)));

  const remaining = slots.length - bookedSlots.length;
  return {
    slots,
    bookedSlots,
    timezone,
    timezoneLabel: formatTimezoneDisplay(timezone),
    fullyBooked: slots.length === 0 || remaining <= 0,
  };
}
